import { revokeSession } from '@/api/login';
import { fetchMe } from '@/api/me';
import type { SessionUser } from '@/api/profile/types';
import { seedTablePreferencesFromSession } from '@/hooks/useTablePreferences';
import { changeLocale } from '@/lib/utils/i18n';
import { shutdownIntercom } from '@/lib/utils/intercom';
import { MANAGEMENT_ROOT } from '@/lib/utils/managementAccess';
import { queryClient } from '@/lib/utils/queryClient';
import { useSessionStore } from '@/store/sessionStore';
import { useUploadStore } from '@/store/uploadStore';

export const ONBOARDING_ROOT = '/onboarding';
export const COMPANY_SEARCH_ROUTE = '/onboarding/company-search';
export const PROFILE_ROOT = '/profile';
/**
 * The login screen lives at the app root: an unauthenticated visitor to `/`
 * sees the form, an authenticated one is forwarded to the dashboard by that
 * route's own guard.
 */
export const LOGIN_ROUTE = '/';
/**
 * Where the OAuth2 callback redirects the browser after an SSO login. Public
 * like `LOGIN_ROUTE` — there is no session yet when it loads, since the page
 * itself is what exchanges the code for one.
 */
export const SSO_CALLBACK_ROUTE = '/single-sign-on-callback';

/**
 * Notification settings, reached from the unsubscribe link in an email.
 *
 * Public by necessity: the recipient has not signed in — that is usually the
 * point — and the link authenticates on the sha256 of their address instead.
 * Behind the auth guard it would bounce to login, which is both a dead
 * unsubscribe link and the kind of thing that draws complaints rather than
 * bug reports.
 */
export const UNSUBSCRIBE_ROUTE = '/unsubscribe';

/**
 * The SSO onboarding portal a customer's IT admin opens from an expiring link
 * (`/sso-onboarding/<token>`), and its test sign-in result page.
 *
 * Public: the admin has no Retraced account — the link's token authorises
 * every call the portal makes.
 */
export const SSO_ONBOARDING_ROOT = '/sso-onboarding';

/** True for the onboarding portal and everything under it. */
export function isSsoOnboardingPath(pathname: string): boolean {
  return (
    pathname === SSO_ONBOARDING_ROOT ||
    pathname.startsWith(`${SSO_ONBOARDING_ROOT}/`)
  );
}

/** True once a `SessionUser` has resolved and has no company membership. */
export function hasCompany(user: SessionUser | null | undefined): boolean {
  return Boolean(user?.company?.id ?? user?.companyId);
}

/**
 * Routes a companyless user may still reach without being bounced to
 * `COMPANY_SEARCH_ROUTE` — the onboarding flow itself, the user's own profile
 * (they need to manage their account even with no company yet), and the
 * management area, whose tools are not company-scoped and whose own guard is
 * the one that decides who gets in.
 */
export function isCompanylessAllowedPath(pathname: string): boolean {
  return (
    pathname.startsWith(ONBOARDING_ROOT) ||
    pathname.startsWith(PROFILE_ROOT) ||
    pathname.startsWith(MANAGEMENT_ROOT)
  );
}

/**
 * Fetches `/api/users/me`, hydrates the session store, seeds table
 * preferences, and applies the user's locale. Extracted so the root route's
 * `beforeLoad` and its re-hydration effect can't drift — a companyless check
 * added to only one of those call sites would leave a hole.
 */
export async function bootstrapSession(): Promise<SessionUser | null> {
  const me = await fetchMe();
  if (!me) return null;

  useSessionStore.getState().setUser(me);
  seedTablePreferencesFromSession(me.preferences);
  const lang =
    (me.preferences?.userLanguage as { language?: string } | undefined)
      ?.language ?? me.langId;
  if (lang) await changeLocale(lang);

  return me;
}

/**
 * Signs the user out and returns them to v2's login screen.
 *
 * v2 revokes the session itself rather than handing off to the old dashboard's
 * `/logout`: that hand-off left this origin, so on any config where the old
 * frontend is remote (staging, production) logging out of localhost dropped the
 * user on the staging dashboard. The old frontend's own copy of the session is
 * not our concern — it 401s and its `/login` now forwards here anyway.
 */
export async function logout(): Promise<void> {
  // Server first: the revoke is authenticated by the token we are about to
  // delete. A failure here (offline, already-expired token) must not trap the
  // user in a session they asked to leave, so it is deliberately swallowed.
  try {
    await revokeSession();
  } catch {
    // ignore — local teardown and the redirect happen either way
  }

  useUploadStore.getState().clearAll();
  shutdownIntercom();
  useSessionStore.getState().clearUser();
  queryClient.clear();
  localStorage.removeItem('retraced:context');

  // `replace`, so Back cannot return to an authenticated page.
  window.location.replace(LOGIN_ROUTE);
}

/**
 * Writes the impersonation into the stored context and reports whether it took.
 *
 * Split out of `beginImpersonation` because one caller needs the write without
 * the full page load that follows it: the hand-off from the old dashboard is
 * already mid-navigation, and re-reading `/users/me` there is cheaper than
 * reloading the document that just booted.
 *
 * Returns false when the context is missing or unparseable — nothing was
 * written, and the caller should not act as though a session had changed.
 */
export function writeImpersonationContext(
  userId: string,
  companyId: string | null,
): boolean {
  const raw = localStorage.getItem('retraced:context');
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as { state?: Record<string, unknown> };
    if (!parsed.state) return false;
    parsed.state.impersonateId = userId;
    parsed.state.companyId = companyId;
    localStorage.setItem('retraced:context', JSON.stringify(parsed));
    return true;
  } catch {
    // Unreadable context: leave it alone and let the auth guard deal with it,
    // rather than writing a session on top of something broken.
    return false;
  }
}

/**
 * Starts impersonating `userId` at `companyId` and drops the operator into the
 * customer app.
 *
 * The counterpart of `endImpersonation`: nothing about the operator's own
 * session moves, and every request from then on carries
 * `x-retraced-impersonate` — which is what makes the API answer as the customer.
 *
 * `companyId` is written too, and it is not optional detail. The picker lists a
 * user once per company they belong to, so "who" does not identify the session
 * on its own — a delegated user has to be impersonated *at* the company that
 * was clicked. Leaving it out sent every following request without a company:
 * `/users/me` could resolve back to the operator, and the customer-area guard
 * then bounced the whole thing to `/management`, which read as impersonation
 * silently doing nothing.
 *
 * Written even when null, so a company left over from an earlier switch cannot
 * leak in and scope the impersonation to a company the target is not in.
 *
 * A full load rather than a router navigation: the client app's caches and
 * stores were populated as the operator, and the root route re-bootstraps
 * `/users/me` — as the customer this time — on entry.
 */
export function beginImpersonation(
  userId: string,
  companyId: string | null,
): void {
  if (!writeImpersonationContext(userId, companyId)) return;

  useUploadStore.getState().clearAll();
  useSessionStore.getState().clearUser();
  queryClient.clear();
  window.location.replace('/dashboard');
}

/**
 * Ends an impersonated session and returns the operator to the management area.
 *
 * Impersonation is an overlay, not a separate login: the access token belongs to
 * the Retraced user the whole time, and it is `impersonateId` — plus the company
 * scoping that arrived with it — that makes every request answer as the
 * customer. So ending it clears those two fields and nothing else. Deleting
 * the whole `retraced:context` (as this used to) threw the operator's own tokens
 * away too, which is why it read as being logged out.
 *
 * This mirrors the old dashboard's `stopImpersonate`, which sets exactly the
 * same two to null and refetches `/me`.
 */
export function endImpersonation(): void {
  useUploadStore.getState().clearAll();
  shutdownIntercom();

  const raw = localStorage.getItem('retraced:context');
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as { state?: Record<string, unknown> };
      if (parsed.state) {
        parsed.state.impersonateId = null;
        parsed.state.companyId = null;
        localStorage.setItem('retraced:context', JSON.stringify(parsed));
      }
    } catch {
      // A context we cannot parse is one we cannot repair. Fall through: the
      // reload's auth guard sends them to login if the token is unusable, which
      // beats leaving them stuck as someone else.
    }
  }

  // The impersonated user is still in the persisted store — clearing it stops
  // their name and company flashing in the header until `/users/me` answers.
  useSessionStore.getState().clearUser();
  queryClient.clear();

  // A full load rather than a router navigation: every cached query and derived
  // value was scoped to the customer's company, and entering fresh makes the
  // root route re-bootstrap `/users/me` — as the operator this time.
  // `replace`, so Back cannot return to a page rendered as someone else.
  window.location.replace(MANAGEMENT_ROOT);
}
